/* clone emit.cacheUntil emit.if emit.ifListening emit.withContext on.all once */
import { Track } from "../../../../../../../utils.js";
export { Track };
const DESTROY_ALL = Symbol("EventEmitter.DESTROY_ALL");
const LISTEN_ALL = Symbol("EventEmitter.LISTEN_ALL");
export default class EventEmitterConfiguration {
    #listeners = new Map();
    #cache = new Map();
    constructor() {
        this.destroy = this.destroy.bind(this);
        this.clone = this.clone.bind(this);
    }
    get on() {
        const on = (name, handler) => {
            if (!this.#listeners.has(name))
                this.#listeners.set(name, new Set());
            if (handler) {
                this.#listeners.get(name).add(handler);
                if (this.#cache.has(name)) {
                    for (const [_name, data, context] of this.#cache.get(name))
                        if (name === LISTEN_ALL)
                            handler(_name, data, context);
                        else
                            handler(data, context);
                }
            }
            return {
                and: this,
                off: () => this.off(name, handler),
            };
        };
        return Object.assign(on, {
            // Cannot return this type: The inferred type of 'on' references an
            // inaccessible 'this' type. A type annotation is necessary.
            all: (handler) => this.on(LISTEN_ALL, handler),
        });
    }
    get once() {
        return Object.assign((name, handler) => {
            const { off, and } = this.on(name, (...args) => {
                off();
                return handler(...args);
            });
            return { off, and };
        }, {
            all: (handler) => {
                const { off, and } = this.on.all((...args) => {
                    off();
                    return handler?.(...args);
                });
                return { off, and };
            },
        });
    }
    get off() {
        return Object.assign((name, handler) => {
            const removed = !!this.#listeners.get(name)?.delete(handler);
            return {
                removed,
                and: this,
            };
        }, {
            all: (handler) => this.off(LISTEN_ALL, handler),
        });
    }
    destroy(name = DESTROY_ALL) {
        if (name === DESTROY_ALL)
            this.#listeners.clear();
        else
            this.#listeners.delete(name);
    }
    get emit() {
        const createEmitter = (options) => {
            const emit = Object.assign((name, ...[data]) => {
                const [_name, _data, _context] = [name, data, options.context];
                const keys = [_name, LISTEN_ALL];
                const results = [];
                for (const key of keys)
                    if (this.#listeners.has(key))
                        if (key === LISTEN_ALL)
                            for (const listener of this.#listeners.get(key)) {
                                const r = listener(_name, _data, _context);
                                if (r instanceof Track)
                                    results.push(r.value);
                            }
                        else
                            for (const listener of this.#listeners.get(key))
                                results.push(listener(_data, _context));
                if (options.cacheUntil) {
                    // Make reference for emitted data, this will allow for easy expiration
                    const tracked = [_name, _data, _context];
                    for (const key of keys) {
                        if (!this.#cache.has(key))
                            this.#cache.set(key, new Set());
                        this.#cache.get(key).add(tracked);
                    }
                    options.cacheUntil.then(() => {
                        for (const key of keys)
                            this.#cache.get(key).delete(tracked);
                    });
                }
                return {
                    result: results[0],
                    results,
                    and: this,
                };
            }, {
                cacheUntil: (promise) => createEmitter({ ...options, cacheUntil: promise }),
                withContext: (context) => createEmitter({ ...options, context }),
                ifListening: (name, data) => {
                    if (this.#listeners.has(name) || this.#listeners.has(LISTEN_ALL))
                        return emit(name, data());
                    return [];
                },
                /**
                 * Too difficult to type fully, this method assumes that argument already has
                 * types enforced
                 */
                if(event) {
                    if (event)
                        return emit(...event);
                },
            });
            return emit;
        };
        return createEmitter({});
    }
    clone() {
        const clone = new this.constructor();
        for (const [key, value] of this.#listeners)
            clone.#listeners.set(key, new Set(value));
        return clone;
    }
}
